import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

function Field({ field, value, onChange }) {
  const id = 'cargo-profile-field-' + field.key;
  const common = { id, value: value ?? '', onChange: function (e) { onChange(field.key, e.target.value); }, 'data-testid': id, className: 'rounded-sm' };
  let control;
  if (field.type === 'select') {
    control = (
      <select {...common} className="flex h-10 w-full rounded-sm border border-input bg-background px-3 text-sm">
        <option value="">—</option>
        {field.options.map(function (o) { return <option key={o} value={o}>{o}</option>; })}
      </select>
    );
  } else if (field.type === 'textarea') {
    control = <Textarea {...common} rows={3} />;
  } else {
    control = <Input {...common} type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} step={field.type === 'number' ? 'any' : undefined} className={'rounded-sm h-10 ' + (field.type === 'number' ? 'sv-mono' : '')} />;
  }
  return (
    <div className={'space-y-1 ' + (field.type === 'textarea' ? 'sm:col-span-2' : '')}>
      <Label htmlFor={id} className="text-[13px] text-gray-700">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      {control}
      {field.hint && <p className="text-[11px] text-gray-500 leading-snug">{field.hint}</p>}
    </div>
  );
}

// Per-product unit prices for the commercial invoice (falls back to the default unit price when blank).
function LinePrices({ lines, unit, values, onChange }) {
  const prices = values.line_prices || {};
  function set(i, v) { onChange('line_prices', { ...prices, [String(i)]: v }); }
  return (
    <div className="sm:col-span-2 border border-gray-200 rounded-sm overflow-hidden" data-testid="cargo-profile-line-prices">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.06em] text-gray-500"><tr><th className="text-left px-3 py-2 font-medium">Product</th><th className="text-left px-3 py-2 font-medium">HS</th><th className="text-right px-3 py-2 font-medium">Quantity</th><th className="text-right px-3 py-2 font-medium">Unit price / {unit}</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {lines.map(function (l, i) {
            const qty = unit === 'MT' ? (l.gross_weight_kg ? l.gross_weight_kg / 1000 : null) : (l.net_weight_kg || l.gross_weight_kg);
            return (
              <tr key={i} data-testid={'cargo-profile-line-' + i}>
                <td className="px-3 py-2 text-ink">{l.description}</td>
                <td className="px-3 py-2 sv-mono text-gray-700">{l.hs_code ? l.hs_code.slice(0, 4) + '.' + l.hs_code.slice(4, 6) : '—'}</td>
                <td className="px-3 py-2 text-right sv-mono text-gray-700">{qty ? qty.toLocaleString() + ' ' + unit : '—'}</td>
                <td className="px-3 py-2 text-right"><Input type="number" step="any" min="0" value={prices[String(i)] ?? ''} placeholder={values.unit_price ? String(values.unit_price) : 'default'} onChange={function (e) { set(i, e.target.value); }} className="rounded-sm h-9 w-32 ml-auto sv-mono text-right" data-testid={'cargo-profile-line-price-' + i} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Config-driven compliance profile: fields come from the backend rule pack for the selected lane.
export function ProfileForm({ data, onSave, saving }) {
  const [values, setValues] = useState(data.profile || {});
  useEffect(function () { setValues(data.profile || {}); }, [data.profile]);
  const dirty = JSON.stringify(values) !== JSON.stringify(data.profile || {});
  function change(key, value) { setValues(function (p) { return { ...p, [key]: value }; }); }
  const lines = data.cargo_lines || [];
  return (
    <form onSubmit={function (e) { e.preventDefault(); onSave(values); }} className="space-y-6" data-testid="cargo-profile-form">
      <p className="text-sm text-gray-600">Everything below that is not on the bill of lading. Values are pre-filled from the booking and shipping instructions where possible; fields marked * are needed for a complete set. {data.profile_updated_at && <span className="text-gray-500">Last saved {new Date(data.profile_updated_at).toLocaleString()}.</span>}</p>
      {data.sections.map(function (section) {
        const fields = data.fields.filter(function (f) { return f.section === section.key; });
        if (!fields.length) return null;
        return (
          <fieldset key={section.key} className="border border-gray-200 rounded-sm p-4" data-testid={'cargo-profile-section-' + section.key}>
            <legend className="px-2 text-xs uppercase tracking-[0.08em] text-gray-500">{section.label}</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map(function (f) { return <Field key={f.key} field={f} value={values[f.key]} onChange={change} />; })}
              {section.key === 'commercial' && lines.length > 1 && <LinePrices lines={lines} unit={data.bulk ? 'MT' : 'kg'} values={values} onChange={change} />}
            </div>
          </fieldset>
        );
      })}
      <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-white/90 backdrop-blur py-3">
        {dirty && <span className="text-xs text-amber-800" data-testid="cargo-profile-dirty">Unsaved changes</span>}
        <Button type="submit" disabled={saving} className="rounded-sm bg-ink hover:bg-ink-800" data-testid="cargo-profile-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save profile</>}</Button>
      </div>
    </form>
  );
}
